import React, { useState, useRef, useCallback } from 'react';
import { MicrophoneIcon, StopIcon } from './CustomIcons';

interface VoiceRecorderResult {
  transcription: string;
  audioBlob: Blob;
  mimeType: string;
}

interface VoiceRecorderProps {
  onTranscriptionReceived: (result: VoiceRecorderResult) => void;
  onError: (error: string) => void;
  disabled?: boolean;
}

export const VoiceRecorder: React.FC<VoiceRecorderProps> = ({
  onTranscriptionReceived,
  onError,
  disabled = false
}) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        } 
      });
      
      // Initialize MediaRecorder with better compatibility
      let mediaRecorder: MediaRecorder;
      let actualMimeType = 'audio/webm';
      try {
        // Try to use the most compatible format first
        if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
          mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm;codecs=opus' });
          actualMimeType = 'audio/webm;codecs=opus';
        } else if (MediaRecorder.isTypeSupported('audio/webm')) {
          mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
          actualMimeType = 'audio/webm';
        } else if (MediaRecorder.isTypeSupported('audio/mp4')) {
          mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/mp4' });
          actualMimeType = 'audio/mp4';
        } else {
          // Fallback to default
          mediaRecorder = new MediaRecorder(stream);
          actualMimeType = mediaRecorder.mimeType || 'audio/webm';
        }
      } catch (e) {
        console.warn('Failed to create MediaRecorder with specific format, using default:', e);
        mediaRecorder = new MediaRecorder(stream);
        actualMimeType = mediaRecorder.mimeType || 'audio/webm';
      }
      
      console.log('MediaRecorder created with mimeType:', actualMimeType);
      
      audioChunksRef.current = [];
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: actualMimeType });
        console.log('Audio recorded with type:', actualMimeType, 'Size:', audioBlob.size);
        await transcribeAudio(audioBlob, actualMimeType);
        
        // Stop all tracks to release microphone
        stream.getTracks().forEach(track => track.stop());
      };
      
      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start(100); // Collect data every 100ms
      setIsRecording(true);
    } catch (error) {
      console.error('Error starting recording:', error);
      
      // Provide more specific error messages
      if (error instanceof Error) {
        if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
          onError('Microphone access denied. Please allow microphone permissions and try again.');
        } else if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
          onError('No microphone found. Please check your microphone connection.');
        } else if (error.name === 'NotSupportedError') {
          onError('Audio recording not supported in this browser.');
        } else {
          onError(`Recording failed: ${error.message}`);
        }
      } else {
        onError('Failed to start recording. Please check microphone permissions.');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onError]);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setIsProcessing(true);
    }
  }, [isRecording]);

  const transcribeAudio = async (audioBlob: Blob, mimeType: string) => {
    try {
      const formData = new FormData();
      const fileExtension = mimeType.includes('mp4') ? 'mp4' : 'webm';
      formData.append('audio', audioBlob, `recording.${fileExtension}`);

      const response = await fetch('/api/problems/transcribe-audio', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`Transcription failed: ${response.statusText}`);
      }

      const result = await response.json();
      
      // Handle direct string response from transcription API
      let transcriptionText = '';
      if (typeof result === 'string' && result.trim()) {
        transcriptionText = result;
      } else if (result.data?.transcription && result.data.transcription.trim()) {
        transcriptionText = result.data.transcription;
      } else {
        throw new Error('Invalid transcription response - no text received');
      }
      
      // Pass back transcription with audio data
      onTranscriptionReceived({
        transcription: transcriptionText,
        audioBlob,
        mimeType
      });
    } catch (error) {
      console.error('Transcription error:', error);
      
      // Provide more specific transcription error messages
      if (error instanceof Error) {
        if (error.message.includes('network') || error.message.includes('fetch')) {
          onError('Network error during transcription. Please check your connection and try again.');
        } else if (error.message.includes('format') || error.message.includes('codec')) {
          onError('Audio format not supported for transcription. Please try recording again.');
        } else if (error.message.includes('timeout')) {
          onError('Transcription timed out. Please try with a shorter recording.');
        } else {
          onError(`Transcription failed: ${error.message}`);
        }
      } else {
        onError('Failed to transcribe audio. Please try again.');
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRecordClick = () => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  return (
    <div className="voice-recorder">
      <div className="voice-recorder-controls">
        <button
          className={`voice-record-btn ${isRecording ? 'recording' : ''} ${isProcessing ? 'processing' : ''}`}
          onClick={handleRecordClick}
          disabled={disabled || isProcessing}
          title={isRecording ? 'Stop recording' : 'Click to start voice practice'}
        >
          {isProcessing ? (
            <>
              <div className="spinner"></div>
              <span>Processing...</span>
            </>
          ) : isRecording ? (
            <>
              <StopIcon size={16} className="recording-icon" />
              <span>Stop</span>
            </>
          ) : (
            <>
              <MicrophoneIcon size={16} className="microphone-icon" />
              <span>Voice</span>
            </>
          )}
        </button>
        
        {isRecording && (
          <div className="voice-recording-indicator">
            <div className="recording-pulse"></div>
            <span>Recording...</span>
          </div>
        )}
      </div>
    </div>
  );
};